import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ShoppingBag,
  Minus,
  Plus,
  ChevronLeft,
  ChevronRight,
  Pill,
  AlertTriangle,
  Truck,
  Heart,
  Share2,
  ShieldCheck,
  Store,
  PackageCheck,
  Check,
  Star,
  BadgePercent,
} from "lucide-react";
import { toast } from "sonner";
import api, { imgUrl, eur } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useSettings } from "../context/SettingsContext";
import ProductCard from "../components/storefront/ProductCard";

const SITE_URL = "https://farmakeiokatirtzoglou.com";

const SECTION_LABELS = [
  {
    key: "ingredients",
    label: "Συστατικά / Σύνθεση",
    aliases: [
      "συστατικα",
      "συνθεση",
      "σύνθεση",
      "συστατικά",
    ],
  },
  {
    key: "usage",
    label: "Τρόπος χρήσης",
    aliases: [
      "τροπος χρησης",
      "τρόπος χρήσης",
      "οδηγιες χρησης",
      "οδηγίες χρήσης",
      "χρηση",
      "χρήση",
    ],
  },
  {
    key: "precautions",
    label: "Προφυλάξεις",
    aliases: [
      "προφυλαξεις",
      "προφυλάξεις",
      "προειδοποιησεις",
      "προειδοποιήσεις",
      "προσοχη",
      "προσοχή",
      "αντενδειξεις",
      "αντενδείξεις",
    ],
  },
];

function normalize(s) {
  return (s || "")
    .trim()
    .toLowerCase()
    .replace(/:$/, "")
    .trim();
}

// =========================================================
// DESCRIPTION PARSER
// =========================================================

function parseDescription(desc) {
  const result = {
    description: "",
    ingredients: "",
    usage: "",
    precautions: "",
  };

  if (!desc) {
    return result;
  }

  const lines = desc.split("\n");
  let current = "description";

  for (const line of lines) {
    const bare = normalize(
      line.replace(
        /[:：].*$/,
        line.includes(":") ? "" : ""
      )
    );

    const headingMatch =
      SECTION_LABELS.find((s) =>
        s.aliases.includes(
          normalize(line.replace(/:.*/, ""))
        )
      ) ||
      SECTION_LABELS.find((s) =>
        s.aliases.includes(bare)
      );

    const colonIdx = line.indexOf(":");
    let matchedInline = null;

    if (colonIdx > -1) {
      const head = normalize(
        line.slice(0, colonIdx)
      );

      matchedInline = SECTION_LABELS.find((s) =>
        s.aliases.includes(head)
      );
    }

    if (matchedInline) {
      current = matchedInline.key;

      const rest = line
        .slice(colonIdx + 1)
        .trim();

      if (rest) {
        result[current] +=
          (result[current] ? "\n" : "") + rest;
      }

      continue;
    }

    if (headingMatch) {
      current = headingMatch.key;
      continue;
    }

    result[current] +=
      (result[current] ? "\n" : "") + line;
  }

  for (const k of Object.keys(result)) {
    result[k] = result[k].trim();
  }

  return result;
}

// =========================================================
// PRODUCT CAROUSEL
// =========================================================

function Carousel({ title, products }) {
  const ref = useRef(null);

  if (!products || products.length === 0) {
    return null;
  }

  const scroll = (dir) => {
    if (ref.current) {
      ref.current.scrollBy({
        left: dir * 320,
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="mt-16">
      <div className="flex items-end justify-between mb-5">
        <h2 className="font-serif-display text-2xl lg:text-3xl text-[#1E3A2F]">
          {title}
        </h2>

        <div className="hidden sm:flex gap-2">
          <button
            onClick={() => scroll(-1)}
            className="p-2 rounded-full border border-[#E5E9E4] hover:bg-[#E8F0E1]"
            aria-label="Προηγούμενα προϊόντα"
          >
            <ChevronLeft className="h-4 w-4 text-[#1E3A2F]" />
          </button>

          <button
            onClick={() => scroll(1)}
            className="p-2 rounded-full border border-[#E5E9E4] hover:bg-[#E8F0E1]"
            aria-label="Επόμενα προϊόντα"
          >
            <ChevronRight className="h-4 w-4 text-[#1E3A2F]" />
          </button>
        </div>
      </div>

      <div
        ref={ref}
        className="flex gap-4 overflow-x-auto pb-3 snap-x scroll-smooth"
        style={{ scrollbarWidth: "thin" }}
      >
        {products.map((p) => (
          <div
            key={p.id}
            className="snap-start shrink-0 w-44 sm:w-52"
          >
            <ProductCard product={p} />
          </div>
        ))}
      </div>
    </section>
  );
}

// =========================================================
// PRODUCT DETAIL
// =========================================================

export default function ProductDetail() {
  const { slug, id } = useParams();
  const productIdentifier = slug || id;

  const nav = useNavigate();

  const { addItem, subtotal } = useCart();
  const { has, toggle } = useWishlist();
  const { settings } = useSettings();

  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState("description");

  const [zoom, setZoom] = useState({
    active: false,
    x: 50,
    y: 50,
  });

  const [categories, setCategories] = useState([]);
  const [related, setRelated] = useState([]);
  const [sameBrand, setSameBrand] = useState([]);
  const [recent, setRecent] = useState([]);
  const [showSticky, setShowSticky] = useState(false);

  const addRef = useRef(null);

  // =========================================================
  // LOAD PRODUCT + CATEGORIES
  // =========================================================

  useEffect(() => {
    setQty(1);
    setActive(0);
    setTab("description");
    setProduct(null);
    setNotFound(false);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    api
      .get(`/products/${productIdentifier}`)
      .then(({ data }) => {
        setProduct(data);
      })
      .catch(() => {
        setNotFound(true);
      });

    api
      .get("/categories")
      .then(({ data }) => {
        setCategories(data || []);
      })
      .catch(() => {
        setCategories([]);
      });
  }, [productIdentifier]);

  // =========================================================
  // CATEGORY MAP
  // =========================================================

  const categoryMap = useMemo(() => {
    const map = {};

    categories.forEach((c) => {
      map[c.id] = c;
    });

    return map;
  }, [categories]);

  // =========================================================
  // PRODUCT CATEGORY
  // =========================================================

  const productCategory = useMemo(() => {
    if (!product) {
      return null;
    }

    /*
     * Κρατάμε ως κύρια κατηγορία την category_id
     * που ήδη χρησιμοποιεί το προϊόν.
     */

    return (
      categories.find(
        (c) => c.id === product.category_id
      ) || null
    );
  }, [categories, product]);

  // =========================================================
  // BUILD CATEGORY SEO PATH
  // =========================================================

  /*
   * Δημιουργεί το πλήρες SEO URL της κατηγορίας.
   *
   * Παράδειγμα:
   *
   * face-care
   *
   * -> /category/face-care
   *
   * cleansers με parent face-care
   *
   * -> /category/face-care/cleansers
   *
   * intimate-care με parent womens-care
   *
   * -> /category/womens-care/intimate-care
   */

  const getCategoryPath = (categoryItem) => {
    if (!categoryItem) {
      return "/category";
    }

    const parts = [];
    const visited = new Set();

    let current = categoryItem;

    while (
      current &&
      !visited.has(current.id)
    ) {
      visited.add(current.id);

      if (current.slug) {
        parts.unshift(current.slug);
      }

      if (!current.parent_id) {
        break;
      }

      current = categoryMap[current.parent_id];
    }

    if (parts.length === 0) {
      return "/category";
    }

    return `/category/${parts.join("/")}`;
  };

  // =========================================================
  // CATEGORY BREADCRUMB TREE
  // =========================================================

  const categoryBreadcrumbs = useMemo(() => {
    if (!productCategory) {
      return [];
    }

    const result = [];
    const visited = new Set();

    let current = productCategory;

    while (
      current &&
      !visited.has(current.id)
    ) {
      visited.add(current.id);

      result.unshift(current);

      if (!current.parent_id) {
        break;
      }

      current = categoryMap[current.parent_id];
    }

    return result;
  }, [
    productCategory,
    categoryMap,
  ]);

  // =========================================================
  // RELATED PRODUCTS
  // =========================================================

  useEffect(() => {
    if (!product) {
      return;
    }

    if (product.category_id) {
      api
        .get("/products", {
          params: {
            category_id:
              product.category_id,
          },
        })
        .then(({ data }) => {
          setRelated(
            (data || [])
              .filter(
                (p) => p.id !== product.id
              )
              .slice(0, 12)
          );
        })
        .catch(() => {
          setRelated([]);
        });
    } else {
      setRelated([]);
    }

    // =======================================================
    // SAME BRAND
    // =======================================================

    if (product.brand) {
      api
        .get("/products", {
          params: {
            search: product.brand,
          },
        })
        .then(({ data }) => {
          setSameBrand(
            (data || [])
              .filter(
                (p) =>
                  p.id !== product.id &&
                  p.brand === product.brand
              )
              .slice(0, 12)
          );
        })
        .catch(() => {
          setSameBrand([]);
        });
    } else {
      setSameBrand([]);
    }

    // =======================================================
    // RECENTLY VIEWED
    // =======================================================

    try {
      const stored = JSON.parse(
        localStorage.getItem(
          "recentlyViewed"
        ) || "[]"
      );

      setRecent(
        stored
          .filter(
            (p) => p.id !== product.id
          )
          .slice(0, 8)
      );

      const mini = {
        id: product.id,
        name: product.name,
        price: product.price,
        compare_price:
          product.compare_price,
        images: product.images || [],
        stock: product.stock,
        brand: product.brand,
        slug: product.slug,
      };

      const next = [
        mini,
        ...stored.filter(
          (p) => p.id !== product.id
        ),
      ].slice(0, 12);

      localStorage.setItem(
        "recentlyViewed",
        JSON.stringify(next)
      );
    } catch {}

    // eslint-disable-next-line
  }, [product?.id]);

  // =========================================================
  // STICKY BAR
  // =========================================================

  useEffect(() => {
    if (!addRef.current) {
      return;
    }

    const obs =
      new IntersectionObserver(
        ([e]) =>
          setShowSticky(!e.isIntersecting),
        {
          threshold: 0,
        }
      );

    obs.observe(addRef.current);

    return () => obs.disconnect();
  }, [product]);

  // =========================================================
  // DESCRIPTION SECTIONS
  // =========================================================

  const sections = useMemo(
    () =>
      parseDescription(
        product?.description
      ),
    [product?.description]
  );

  // =========================================================
  // SEO — TITLE / DESCRIPTION / CANONICAL
  // =========================================================

  useEffect(() => {
    if (!product) {
      return;
    }

    const categoryText =
      productCategory?.name
        ? ` | ${productCategory.name}`
        : "";

    document.title =
      `${product.name}${categoryText} | Φαρμακείο Κατιρτζόγλου`;

    const descriptionSource =
      product.description ||
      `Αγοράστε ${product.name}${
        product.brand
          ? ` ${product.brand}`
          : ""
      } από το Φαρμακείο Κατιρτζόγλου.`;

    const metaDescription =
      descriptionSource
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 155);

    let meta =
      document.querySelector(
        'meta[name="description"]'
      );

    if (!meta) {
      meta =
        document.createElement("meta");

      meta.setAttribute(
        "name",
        "description"
      );

      document.head.appendChild(meta);
    }

    meta.setAttribute(
      "content",
      metaDescription
    );

    // =======================================================
    // CANONICAL
    // =======================================================

    const canonicalUrl =
      `${SITE_URL}/product/${
        product.slug ||
        productIdentifier
      }`;

    let canonical =
      document.querySelector(
        'link[rel="canonical"]'
      );

    if (!canonical) {
      canonical =
        document.createElement("link");

      canonical.setAttribute(
        "rel",
        "canonical"
      );

      document.head.appendChild(
        canonical
      );
    }

    canonical.setAttribute(
      "href",
      canonicalUrl
    );
  }, [
    product,
    productCategory,
    productIdentifier,
  ]);

  // =========================================================
  // PRODUCT STRUCTURED DATA
  // =========================================================

  useEffect(() => {
    if (!product) {
      return;
    }

    const productUrl =
      `${SITE_URL}/product/${
        product.slug ||
        productIdentifier
      }`;

    const productImages =
      (product.images || [])
        .map((image) => {
          try {
            return imgUrl(image);
          } catch {
            return null;
          }
        })
        .filter(Boolean);

    const availability =
      product.stock > 0
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock";

    const productSchema = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description:
        product.description ||
        product.name,
      url: productUrl,

      ...(productImages.length > 0
        ? {
            image: productImages,
          }
        : {}),

      ...(product.brand
        ? {
            brand: {
              "@type": "Brand",
              name: product.brand,
            },
          }
        : {}),

      ...(product.sku
        ? {
            sku: product.sku,
          }
        : {}),

      offers: {
        "@type": "Offer",
        url: productUrl,
        priceCurrency: "EUR",
        price: Number(
          product.price
        ).toFixed(2),
        availability,
        itemCondition:
          "https://schema.org/NewCondition",
      },
    };

    let script =
      document.getElementById(
        "product-structured-data"
      );

    if (!script) {
      script =
        document.createElement(
          "script"
        );

      script.id =
        "product-structured-data";

      script.type =
        "application/ld+json";

      document.head.appendChild(
        script
      );
    }

    script.textContent =
      JSON.stringify(
        productSchema
      );

    return () => {
      const existing =
        document.getElementById(
          "product-structured-data"
        );

      if (existing) {
        existing.remove();
      }
    };
  }, [
    product,
    productIdentifier,
  ]);

  // =========================================================
  // BREADCRUMB STRUCTURED DATA
  // =========================================================

  useEffect(() => {
    if (!product) {
      return;
    }

    const productUrl =
      `${SITE_URL}/product/${
        product.slug ||
        productIdentifier
      }`;

    const itemListElement = [
      {
        "@type": "ListItem",
        position: 1,
        name: "Αρχική",
        item: `${SITE_URL}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Προϊόντα",
        item: `${SITE_URL}/shop`,
      },
    ];

    categoryBreadcrumbs.forEach(
      (category, index) => {
        itemListElement.push({
          "@type": "ListItem",
          position: index + 3,
          name: category.name,
          item:
            `${SITE_URL}${getCategoryPath(
              category
            )}`,
        });
      }
    );

    itemListElement.push({
      "@type": "ListItem",
      position:
        itemListElement.length + 1,
      name: product.name,
      item: productUrl,
    });

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement,
    };

    let script =
      document.getElementById(
        "product-breadcrumb-schema"
      );

    if (!script) {
      script =
        document.createElement(
          "script"
        );

      script.id =
        "product-breadcrumb-schema";

      script.type =
        "application/ld+json";

      document.head.appendChild(
        script
      );
    }

    script.textContent =
      JSON.stringify(
        breadcrumbSchema
      );

    return () => {
      const existing =
        document.getElementById(
          "product-breadcrumb-schema"
        );

      if (existing) {
        existing.remove();
      }
    };
  }, [
    product,
    productIdentifier,
    categoryBreadcrumbs,
    categoryMap,
  ]);

  // =========================================================
  // NOT FOUND
  // =========================================================

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="text-[#536D61]">
          Το προϊόν δεν βρέθηκε.
        </p>

        <Link
          to="/shop"
          className="text-[#1E3A2F] underline mt-4 inline-block"
        >
          Επιστροφή στα προϊόντα
        </Link>
      </div>
    );
  }

  // =========================================================
  // LOADING
  // =========================================================

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center text-[#536D61]">
        Φόρτωση...
      </div>
    );
  }

  const images =
    product.images || [];

  const onSale =
    product.compare_price &&
    product.compare_price >
      product.price;

  const discount = onSale
    ? Math.round(
        (1 -
          product.price /
            product.compare_price) *
          100
      )
    : 0;

  const wished = has(product.id);

  const categoryName =
    productCategory?.name || "";

  const stockBadge =
    product.stock <= 0
      ? {
          color: "#C25953",
          dot: "#C25953",
          label: "Εξαντλημένο",
        }
      : product.stock <= 5
      ? {
          color: "#8a5a1e",
          dot: "#d08a32",
          label: `Περιορισμένο απόθεμα (${product.stock} τεμ.)`,
        }
      : {
          color: "#4A5D23",
          dot: "#4A5D23",
          label: "Άμεσα διαθέσιμο",
        };

  // =========================================================
  // CART ACTIONS
  // =========================================================

  const add = () => {
    addItem(product, qty);

    toast.success(
      "Προστέθηκε στο καλάθι"
    );
  };

  const buyNow = () => {
    addItem(product, qty);
    nav("/checkout");
  };

  // =========================================================
  // WISHLIST
  // =========================================================

  const fav = () => {
    toggle(product);

    toast.success(
      wished
        ? "Αφαιρέθηκε από τα αγαπημένα"
        : "Προστέθηκε στα αγαπημένα"
    );
  };

  // =========================================================
  // SHARE
  // =========================================================

  const share = async () => {
    const url =
      window.location.href;

    try {
      if (navigator.share) {
        await navigator.share({
          title: product.name,
          url,
        });
      } else {
        await navigator.clipboard.writeText(
          url
        );

        toast.success(
          "Ο σύνδεσμος αντιγράφηκε"
        );
      }
    } catch {}
  };

  // =========================================================
  // SHIPPING
  // =========================================================

  const threshold =
    settings?.free_shipping_threshold ??
    50;

  const remaining = Math.max(
    0,
    threshold - subtotal
  );

  const progress = Math.min(
    100,
    threshold > 0
      ? (subtotal / threshold) *
          100
      : 100
  );

  // =========================================================
  // TABS
  // =========================================================

  const tabs = [
    {
      key: "description",
      label: "Περιγραφή",
      content:
        sections.description ||
        product.description,
    },
    {
      key: "ingredients",
      label: "Συστατικά / Σύνθεση",
      content:
        sections.ingredients,
    },
    {
      key: "usage",
      label: "Τρόπος χρήσης",
      content: sections.usage,
    },
    {
      key: "precautions",
      label: "Προφυλάξεις",
      content:
        sections.precautions,
    },
    {
      key: "extra",
      label: "Επιπλέον πληροφορίες",
      content: "extra",
    },
  ].filter((t) =>
    t.key === "extra"
      ? true
      : t.content &&
        t.content.trim()
  );

  const activeTab = tabs.find(
    (t) => t.key === tab
  )
    ? tab
    : tabs[0]?.key || "extra";

  // =========================================================
  // TRUST
  // =========================================================

  const trust = [
    {
      icon: ShieldCheck,
      label: "Ασφαλείς πληρωμές",
    },
    {
      icon: Truck,
      label: "Παράδοση 24–48 ώρες",
    },
    {
      icon: Store,
      label: "Click & Collect",
    },
    {
      icon: PackageCheck,
      label: "Εγγυημένο φαρμακείο",
    },
  ];

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>
      <div className="bg-white">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8">

          {/* =====================================================
              BREADCRUMB
          ====================================================== */}

          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-1.5 text-sm text-[#536D61] mb-6 flex-wrap"
          >

            <Link
              to="/"
              className="hover:text-[#1E3A2F]"
            >
              Αρχική
            </Link>

            <ChevronRight className="h-3.5 w-3.5 shrink-0" />

            <Link
              to="/shop"
              className="hover:text-[#1E3A2F]"
            >
              Προϊόντα
            </Link>

            {categoryBreadcrumbs.map(
              (category, index) => (
                <React.Fragment
                  key={category.id}
                >

                  <ChevronRight className="h-3.5 w-3.5 shrink-0" />

                  <Link
                    to={getCategoryPath(
                      category
                    )}
                    className={
                      index ===
                      categoryBreadcrumbs.length -
                        1
                        ? "text-[#1E3A2F] hover:underline"
                        : "hover:text-[#1E3A2F]"
                    }
                  >
                    {category.name}
                  </Link>

                </React.Fragment>
              )
            )}

            <ChevronRight className="h-3.5 w-3.5 shrink-0" />

            <span className="text-[#1E3A2F] truncate max-w-[220px]">
              {product.name}
            </span>

          </nav>

          {/* =====================================================
              PRODUCT
          ====================================================== */}

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">

            {/* ===================================================
                GALLERY
            ==================================================== */}

            <div>

              <div
                className="relative aspect-square rounded-2xl overflow-hidden border border-[#E5E9E4] bg-white shadow-sm group"
                onMouseEnter={() =>
                  images[active] &&
                  setZoom((z) => ({
                    ...z,
                    active: true,
                  }))
                }
                onMouseLeave={() =>
                  setZoom((z) => ({
                    ...z,
                    active: false,
                  }))
                }
                onMouseMove={(e) => {
                  const r =
                    e.currentTarget.getBoundingClientRect();

                  setZoom({
                    active: true,
                    x:
                      ((e.clientX -
                        r.left) /
                        r.width) *
                      100,
                    y:
                      ((e.clientY -
                        r.top) /
                        r.height) *
                      100,
                  });
                }}
              >

                {images[active] ? (
                  <img
                    src={imgUrl(
                      images[active]
                    )}
                    alt={`${product.name} - Φαρμακείο Κατιρτζόγλου`}
                    loading="eager"
                    className="h-full w-full object-cover transition-transform duration-200 ease-out"
                    style={
                      zoom.active
                        ? {
                            transform:
                              "scale(1.8)",
                            transformOrigin: `${zoom.x}% ${zoom.y}%`,
                          }
                        : {
                            transform:
                              "scale(1)",
                          }
                    }
                    data-testid="product-main-image"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center bg-[#E8F0E1]">
                    <Pill className="h-20 w-20 text-[#1E3A2F]/30" />
                  </div>
                )}

                {discount > 0 && (
                  <span
                    className="absolute top-4 left-4 inline-flex items-center gap-1 bg-[#C25953] text-white text-sm font-bold px-3 py-1.5 rounded-full shadow"
                    data-testid="discount-badge"
                  >
                    <BadgePercent className="h-4 w-4" />
                    -{discount}%
                  </span>
                )}

                {images[active] && (
                  <span className="absolute bottom-3 right-3 text-[11px] text-[#536D61] bg-white/85 px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    Μεγέθυνση με το ποντίκι
                  </span>
                )}

              </div>

              {images.length > 1 && (
                <div className="flex gap-3 mt-4 overflow-x-auto pb-1">

                  {images.map((im, i) => (
                    <button
                      key={i}
                      onClick={() =>
                        setActive(i)
                      }
                      data-testid={`thumb-${i}`}
                      className={`h-20 w-20 shrink-0 rounded-xl overflow-hidden border-2 transition-colors ${
                        active === i
                          ? "border-[#1E3A2F]"
                          : "border-[#E5E9E4] hover:border-[#CBD9B6]"
                      }`}
                    >
                      <img
                        src={imgUrl(im)}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}

                </div>
              )}

            </div>

            {/* ===================================================
                INFO
            ==================================================== */}

            <div>

              {product.brand && (
                <Link
                  to={`/shop?search=${encodeURIComponent(
                    product.brand
                  )}`}
                  data-testid="brand-link"
                  className="inline-block text-xs uppercase tracking-[0.2em] text-[#536D61] mb-2 hover:text-[#1E3A2F] underline-offset-4 hover:underline"
                >
                  {product.brand}
                </Link>
              )}

              <h1 className="font-serif-display text-3xl lg:text-[2.6rem] leading-tight text-[#1E3A2F]">
                {product.name}
              </h1>

              <div className="mt-4 flex items-end gap-3 flex-wrap">

                <span
                  className="text-3xl font-bold text-[#1E3A2F]"
                  data-testid="detail-price"
                >
                  {eur(product.price)}
                </span>

                {onSale && (
                  <span className="text-lg text-[#536D61] line-through mb-0.5">
                    {eur(
                      product.compare_price
                    )}
                  </span>
                )}

                {onSale && (
                  <span className="mb-1 text-sm font-semibold text-[#C25953]">
                    Κερδίζετε{" "}
                    {eur(
                      product.compare_price -
                        product.price
                    )}
                  </span>
                )}

              </div>

              <div className="mt-3">

                <span
                  className="inline-flex items-center gap-1.5 text-sm font-medium"
                  style={{
                    color:
                      stockBadge.color,
                  }}
                  data-testid="stock-status"
                >
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{
                      background:
                        stockBadge.dot,
                    }}
                  />

                  {stockBadge.label}
                </span>

              </div>

              {product.prescription_required && (
                <div className="mt-4 flex items-start gap-2 bg-[#FBF3E8] border border-[#E8D9C0] rounded-xl p-3 text-sm text-[#8a5a1e]">

                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />

                  Απαιτείται ιατρική συνταγή για αυτό το προϊόν.

                </div>
              )}

              {/* =================================================
                  FREE SHIPPING
              ================================================== */}

              <div className="mt-5 bg-[#F9F8F6] border border-[#E5E9E4] rounded-xl p-4">

                {remaining > 0 ? (
                  <p className="text-sm text-[#2a3a33]">
                    Προσθέστε{" "}
                    <span className="font-semibold text-[#1E3A2F]">
                      {eur(remaining)}
                    </span>{" "}
                    ακόμη για{" "}
                    <span className="font-semibold text-[#1E3A2F]">
                      ΔΩΡΕΑΝ αποστολή
                    </span>
                    !
                  </p>
                ) : (
                  <p className="text-sm font-medium text-[#4A5D23] flex items-center gap-1.5">
                    <Check className="h-4 w-4" />
                    Δικαιούστε δωρεάν αποστολή
                  </p>
                )}

                <div className="mt-2 h-2 rounded-full bg-[#E5E9E4] overflow-hidden">

                  <div
                    className="h-full bg-[#1E3A2F] rounded-full transition-all duration-500"
                    style={{
                      width: `${progress}%`,
                    }}
                  />

                </div>

              </div>

              {/* =================================================
                  QUANTITY
              ================================================== */}

              <div className="mt-6 flex items-center gap-4">

                <div className="flex items-center border border-[#E5E9E4] rounded-full">

                  <button
                    onClick={() =>
                      setQty((q) =>
                        Math.max(
                          1,
                          q - 1
                        )
                      )
                    }
                    className="p-3 hover:text-[#1E3A2F] text-[#536D61]"
                    data-testid="qty-minus"
                  >
                    <Minus className="h-4 w-4" />
                  </button>

                  <span
                    className="w-10 text-center font-semibold text-[#1E3A2F]"
                    data-testid="qty-value"
                  >
                    {qty}
                  </span>

                  <button
                    onClick={() =>
                      setQty(
                        (q) => q + 1
                      )
                    }
                    className="p-3 hover:text-[#1E3A2F] text-[#536D61]"
                    data-testid="qty-plus"
                  >
                    <Plus className="h-4 w-4" />
                  </button>

                </div>

                <button
                  onClick={fav}
                  data-testid="detail-wishlist"
                  className="inline-flex items-center justify-center p-3 rounded-full border border-[#E5E9E4] hover:border-[#1E3A2F] transition-colors"
                  aria-label="Προσθήκη στα αγαπημένα"
                >
                  <Heart
                    className={`h-5 w-5 ${
                      wished
                        ? "fill-[#C25953] text-[#C25953]"
                        : "text-[#1E3A2F]"
                    }`}
                  />
                </button>

                <button
                  onClick={share}
                  data-testid="share-button"
                  className="inline-flex items-center justify-center p-3 rounded-full border border-[#E5E9E4] hover:border-[#1E3A2F] transition-colors"
                  aria-label="Κοινοποίηση προϊόντος"
                >
                  <Share2 className="h-5 w-5 text-[#1E3A2F]" />
                </button>

              </div>

              {/* =================================================
                  ACTIONS
              ================================================== */}

              <div
                ref={addRef}
                className="mt-4 flex flex-col sm:flex-row gap-3"
              >

                <button
                  onClick={add}
                  disabled={
                    product.stock <= 0
                  }
                  data-testid="detail-add-to-cart"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1E3A2F] text-white px-6 py-4 rounded-full font-semibold hover:bg-[#111C18] transition-colors disabled:opacity-40 shadow-sm"
                >
                  <ShoppingBag className="h-5 w-5" />
                  Προσθήκη στο καλάθι
                </button>

                <button
                  onClick={buyNow}
                  disabled={
                    product.stock <= 0
                  }
                  data-testid="buy-now"
                  className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-[#1E3A2F] text-[#1E3A2F] px-6 py-4 rounded-full font-semibold hover:bg-[#1E3A2F] hover:text-white transition-colors disabled:opacity-40"
                >
                  Άμεση αγορά
                </button>

              </div>

              {/* =================================================
                  TRUST
              ================================================== */}

              <div className="mt-6 grid grid-cols-2 gap-3">

                {trust.map(
                  (t, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2.5 bg-[#E8F0E1]/60 rounded-xl px-3 py-2.5"
                    >

                      <t.icon className="h-5 w-5 text-[#1E3A2F] shrink-0" />

                      <span className="text-sm text-[#1E3A2F] font-medium leading-tight">
                        {t.label}
                      </span>

                    </div>
                  )
                )}

              </div>

            </div>

          </div>

          {/* =====================================================
              TABS
          ====================================================== */}

          <div className="mt-14">

            <div className="flex gap-1 border-b border-[#E5E9E4] overflow-x-auto">

              {tabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() =>
                    setTab(t.key)
                  }
                  data-testid={`tab-${t.key}`}
                  className={`px-4 sm:px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                    activeTab === t.key
                      ? "border-[#1E3A2F] text-[#1E3A2F]"
                      : "border-transparent text-[#536D61] hover:text-[#1E3A2F]"
                  }`}
                >
                  {t.label}
                </button>
              ))}

            </div>

            <div
              className="py-6 max-w-3xl"
              data-testid="tab-content"
            >

              {activeTab === "extra" ? (

                <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">

                  {product.brand && (
                    <div className="flex justify-between border-b border-[#F0F2EE] py-2">

                      <dt className="text-[#536D61]">
                        Μάρκα
                      </dt>

                      <dd className="text-[#1E3A2F] font-medium">
                        {product.brand}
                      </dd>

                    </div>
                  )}

                  {categoryName && (
                    <div className="flex justify-between border-b border-[#F0F2EE] py-2">

                      <dt className="text-[#536D61]">
                        Κατηγορία
                      </dt>

                      <dd className="text-[#1E3A2F] font-medium">
                        {categoryName}
                      </dd>

                    </div>
                  )}

                  {product.sku && (
                    <div className="flex justify-between border-b border-[#F0F2EE] py-2">

                      <dt className="text-[#536D61]">
                        Κωδικός / Barcode
                      </dt>

                      <dd className="text-[#1E3A2F] font-medium font-mono">
                        {product.sku}
                      </dd>

                    </div>
                  )}

                  <div className="flex justify-between border-b border-[#F0F2EE] py-2">

                    <dt className="text-[#536D61]">
                      Διαθεσιμότητα
                    </dt>

                    <dd
                      className="font-medium"
                      style={{
                        color:
                          stockBadge.color,
                      }}
                    >
                      {stockBadge.label}
                    </dd>

                  </div>

                  {!product.brand &&
                    !categoryName &&
                    !product.sku && (
                      <p className="text-[#536D61]">
                        Δεν υπάρχουν επιπλέον πληροφορίες.
                      </p>
                    )}

                </dl>

              ) : (

                <div className="prose-pharm whitespace-pre-line text-[#2a3a33] leading-relaxed">

                  {tabs.find(
                    (t) =>
                      t.key ===
                      activeTab
                  )?.content ||
                    "Δεν υπάρχουν διαθέσιμες πληροφορίες."}

                </div>

              )}

            </div>

          </div>

          {/* =====================================================
              REVIEWS
          ====================================================== */}

          <section className="mt-10 border-t border-[#E5E9E4] pt-10">

            <h2 className="font-serif-display text-2xl lg:text-3xl text-[#1E3A2F] mb-4">
              Αξιολογήσεις πελατών
            </h2>

            <div className="bg-[#F9F8F6] border border-[#E5E9E4] rounded-2xl p-8 text-center">

              <div className="flex justify-center gap-1 mb-3">

                {[...Array(5)].map(
                  (_, i) => (
                    <Star
                      key={i}
                      className="h-5 w-5 text-[#CBD9B6]"
                    />
                  )
                )}

              </div>

              <p className="text-[#536D61]">
                Δεν υπάρχουν αξιολογήσεις ακόμη. Γίνετε ο πρώτος που θα αξιολογήσει αυτό το προϊόν μετά την αγορά σας.
              </p>

            </div>

          </section>

          {/* =====================================================
              RELATED PRODUCTS
          ====================================================== */}

          <Carousel
            title="Σχετικά προϊόντα"
            products={related}
          />

          {sameBrand.length > 0 && (
            <Carousel
              title={`Προϊόντα ${product.brand}`}
              products={sameBrand}
            />
          )}

          {recent.length > 0 && (
            <Carousel
              title="Είδατε πρόσφατα"
              products={recent}
            />
          )}

        </div>

        {/* =====================================================
            STICKY ADD TO CART
        ====================================================== */}

        <div
          className={`fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-[#E5E9E4] shadow-[0_-4px_20px_rgba(0,0,0,0.06)] transition-transform duration-300 ${
            showSticky &&
            product.stock > 0
              ? "translate-y-0"
              : "translate-y-full"
          }`}
          data-testid="sticky-add-bar"
        >

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 flex items-center gap-3">

            <div className="h-12 w-12 rounded-lg overflow-hidden bg-[#E8F0E1] shrink-0 hidden sm:block">

              {images[0] ? (
                <img
                  src={imgUrl(
                    images[0]
                  )}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : null}

            </div>

            <div className="min-w-0 flex-1">

              <p className="text-sm font-medium text-[#1E3A2F] truncate">
                {product.name}
              </p>

              <div className="flex items-center gap-2">

                <span className="text-base font-bold text-[#1E3A2F]">
                  {eur(product.price)}
                </span>

                {onSale && (
                  <span className="text-xs text-[#536D61] line-through">
                    {eur(
                      product.compare_price
                    )}
                  </span>
                )}

              </div>

            </div>

            <button
              onClick={add}
              data-testid="sticky-add-to-cart"
              disabled={
                product.stock <= 0
              }
              className="inline-flex items-center gap-2 bg-[#1E3A2F] text-white px-5 sm:px-7 py-3 rounded-full font-semibold hover:bg-[#111C18] transition-colors shrink-0 disabled:opacity-40"
            >

              <ShoppingBag className="h-4 w-4" />

              <span className="hidden sm:inline">
                Προσθήκη στο καλάθι
              </span>

              <span className="sm:hidden">
                Καλάθι
              </span>

            </button>

          </div>

        </div>

      </div>
    </>
  );
}